<template>
  <transition name="modal">
    <div class="modal-mask tenant-setting-confirm-modal" @click.self="$emit('close')">
      <div class="modal-wrapper">
        <div class="modal-container">
          <div class="modal-heading">
            <p class="eyebrow">CONFIRM SETTINGS</p>
            <h2>自社設定の更新確認</h2>
            <p>以下の内容を現在の会社へ適用します。</p>
          </div>

          <div class="summary-section">
            <h3>会社情報</h3>
            <dl>
              <div><dt>会社名</dt><dd>{{ settings.company_name }}</dd></div>
              <div><dt>会社メール</dt><dd>{{ display(settings.company_email) }}</dd></div>
              <div><dt>電話番号</dt><dd>{{ display(settings.telephone) }}</dd></div>
            </dl>
          </div>

          <div class="summary-section">
            <h3>勤怠計算・打刻</h3>
            <dl>
              <div><dt>定時前打刻猶予</dt><dd>{{ settings.pre_buffer }}</dd></div>
              <div><dt>定時後打刻猶予</dt><dd>{{ settings.post_buffer }}</dd></div>
              <div><dt>時間外の切り上げ単位</dt><dd>{{ settings.work_time_unit }}</dd></div>
              <div><dt>規定勤務時間</dt><dd>{{ settings.work_basic }}</dd></div>
              <div><dt>規定休憩時間</dt><dd>{{ settings.break_basic }}</dd></div>
              <div><dt>定時後猶予を休憩扱い</dt><dd>{{ yesNo(settings.post_work_break_flg) }}</dd></div>
              <div><dt>プロジェクト休憩を自動反映</dt><dd>{{ yesNo(settings.use_project_break_flg) }}</dd></div>
              <div><dt>半日単位の休暇</dt><dd>{{ yesNo(settings.half_day_leave_flg !== false) }}</dd></div>
            </dl>
            <p class="attendance-note">
              未設定時は打刻猶予なし・時間外1分単位・規定勤務8時間・規定休憩1時間を使用します。
            </p>
          </div>

          <div class="summary-section">
            <h3>Slack連携・通知</h3>
            <dl>
              <div><dt>Slackトークン</dt><dd>{{ tokenAction }}</dd></div>
              <div><dt>申請状況の即時通知</dt><dd>{{ onOff(settings.slack_notification_flg) }}</dd></div>
              <div v-if="settings.slack_notification_flg"><dt>即時通知先</dt><dd>{{ settings.slack_channel }}</dd></div>
              <div><dt>承認待ちリマインド</dt><dd>{{ onOff(settings.pending_approval_notification_flg) }}</dd></div>
              <div v-if="settings.pending_approval_notification_flg"><dt>リマインド通知先</dt><dd>{{ settings.pending_approval_slack_channel }}</dd></div>
              <div v-if="settings.pending_approval_notification_flg"><dt>実行時間</dt><dd>会社営業日に1日1回（時刻は就業時間の設定）</dd></div>
              <div><dt>管理部門向け朝通知</dt><dd>{{ onOff(settings.management_daily_notification_flg) }}</dd></div>
              <div v-if="settings.management_daily_notification_flg"><dt>管理部門通知先</dt><dd>{{ settings.management_daily_slack_channel }}</dd></div>
              <!--
                **判定を出していないあいだは、確認の画面にも出さない。**
                編集の画面から消しても、ここに残っていると
                「保存しようとしている中身」として最後に読まれる。
                出していない機能の設定を、更新のたびに見せない。
              -->
              <template v-if="legalComplianceEnabled">
                <div><dt>36協定の上限状況</dt><dd>{{ onOff(settings.overtime_alert_notification_flg) }}</dd></div>
                <!--
                  空欄を空欄のまま見せない。**確認の画面でいちばん困るのは
                  「何も書いていない＝何も起きない」と読めること。**専用の
                  通知先が空なら管理部門向けへ送るし、そちらも空なら届かない。
                  どちらなのかをここで言う。
                -->
                <div v-if="settings.overtime_alert_notification_flg"><dt>上限状況の通知先</dt><dd>{{ overtimeAlertChannelLabel }}</dd></div>
                <div v-if="settings.overtime_alert_notification_flg"><dt>算定期日</dt><dd>{{ overtimeCalculationDueLabel }}</dd></div>
              </template>
            </dl>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" :disabled="loading" @click="$emit('close')">
              戻って修正
            </button>
            <save-button
              :label="'更新する'"
              :loading="loading"
              class="btn btn-primary"
              @click="$emit('update')"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import SaveButton from '../common/SaveButton'

export default {
  name: 'SettingUpdateModal',
  components: { SaveButton },
  props: {
    settings: { type: Object, required: true },
    tokenAction: { type: String, required: true },
    loading: Boolean
  },
  computed: {
    /**
     * **判定を出しているかどうかは、サーバが返した1つで決める。**
     * 画面ごとに設定を読み直すと、書き忘れた画面から漏れる。
     */
    legalComplianceEnabled () {
      return Boolean(this.settings && this.settings.legal_compliance_enabled)
    },
    /**
     * どこへ届くか。**空欄のまま見せない。**
     *
     * 専用の通知先が空なら管理部門向けのチャンネルへ送る。そちらも空なら
     * 本当に届かない。空欄だけを見せると、この2つが同じ見た目になる。
     */
    overtimeAlertChannelLabel () {
      const dedicated = String(this.settings.overtime_alert_slack_channel || '').trim()
      if (dedicated) return dedicated
      /*
       * **管理部門向け朝通知が「入」のときだけ倒れる。**止めていても
       * チャンネルIDの値は残るので、値の有無だけで「そこへ届く」と
       * 書くと、実際には届かないのに届く顔をした確認画面になる。
       */
      const management = this.settings.management_daily_notification_flg
        ? String(this.settings.management_daily_slack_channel || '').trim()
        : ''
      return management
        ? '管理部門向け朝通知と同じチャンネル（' + management + '）'
        : '未設定（このままでは届きません）'
    },
    /**
     * 算定期日の言い方。**数え方と日が揃っていなければ「未設定」。**
     * 片方だけ入った状態を画面で補うと、決めたつもりで1通も来ない。
     */
    overtimeCalculationDueLabel () {
      const day = Number(this.settings.overtime_calculation_due_day)
      if (!this.settings.overtime_calculation_due_kind || !day) { return '未設定' }
      return this.settings.overtime_calculation_due_kind === 'BUSINESS_DAY'
        ? '翌月の第' + day + '営業日'
        : '翌月の' + day + '日'
    }
  },
  methods: {
    display (value) {
      return value == null || String(value).trim() === '' ? '未設定' : value
    },
    yesNo (value) {
      return value ? 'はい' : 'いいえ'
    },
    onOff (value) {
      return value ? 'オン' : 'オフ'
    }
  }
}
</script>

<style scoped>
.modal-mask { position: fixed; z-index: 9998; inset: 0; display: table; width: 100%; height: 100%; background: rgba(26, 45, 57, .58); }
.modal-wrapper { display: table-cell; vertical-align: middle; }
.modal-container { width: min(92vw, 760px); max-height: 90vh; margin: 0 auto; padding: 24px; overflow-y: auto; border-radius: 16px; background: #fff; box-shadow: 0 24px 60px rgba(9, 34, 50, .28); }
.modal-heading { margin-bottom: 18px; }
.modal-heading h2 { margin: 0 0 6px; color: #17384d; font-size: 22px; }
.modal-heading p { margin: 0; color: #647b89; }
.eyebrow { margin-bottom: 4px !important; color: #0a6ca8 !important; font-size: 11px; font-weight: 800; letter-spacing: .14em; }
.summary-section { margin-bottom: 14px; padding: 14px; border: 1px solid #dce7ee; border-radius: 12px; }
.summary-section h3 { margin: 0 0 10px; color: #17384d; font-size: 14px; }
.summary-section dl { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 16px; margin: 0; }
.summary-section dl > div { display: grid; grid-template-columns: minmax(110px, 42%) minmax(0, 1fr); gap: 8px; min-width: 0; padding-top: 7px; border-top: 1px solid #edf2f5; }
.summary-section dt { color: #627987; font-size: 11px; font-weight: 800; }
.summary-section dd { min-width: 0; margin: 0; color: #17384d; font-weight: 700; text-align: right; overflow-wrap: anywhere; }
.attendance-note { margin: 12px 0 0; color: #627987; font-size: 12px; line-height: 1.6; }
.modal-actions { display: flex; justify-content: space-between; gap: 12px; margin-top: 18px; }
.modal-actions .btn { min-width: 150px; min-height: 44px; }

@media (max-width: 767.98px) {
  .modal-container { width: calc(100vw - 16px); max-height: calc(100vh - 16px); padding: 16px; }
  .summary-section dl { grid-template-columns: minmax(0, 1fr); }
  .summary-section dl > div { grid-template-columns: minmax(110px, 42%) minmax(0, 1fr); }
  .modal-actions { flex-direction: column-reverse; }
  .modal-actions .btn { width: 100%; }
}
</style>
